import React, { FC, ReactElement, useMemo } from 'react'
import Flatpickr from 'react-flatpickr'
import classNames from 'classnames'
import { format, isValid } from 'date-fns'
import styles from './DatePicker.module.scss'

type TProps = {
  label?: string
  name?: string
  date: any
  inline?: boolean
  onChange?: (date: any) => void // default value allows this to be optional
  value?: any
  disabled?: boolean
  required?: boolean
}

const DatePicker: FC<TProps> = ({
  label,
  name,
  date,
  inline,
  onChange = (date: any) => {},
  disabled = false,
  required = false,
}): ReactElement => {
  const classes = classNames(styles.root, {
    [styles.inline]: !!inline,
  })

  // Memoise so the options object keeps a stable identity across renders.
  // react-flatpickr re-inits the underlying instance whenever `options`
  // changes identity, which makes the controlled input flash between the
  // raw value and the `dateFormat`-formatted value on every render.
  const options = useMemo(
    () => ({
      name,
      inline,
      dateFormat: 'Y-m-d',
    }),
    [name, inline],
  )

  // react-flatpickr renders a controlled input as `value={value.toString()}`.
  // If we hand it a Date object, `.toString()` is the raw long form
  // ("Wed May 27 2026 00:00:00 GMT+0200…") which React paints before flatpickr
  // rewrites the DOM to the `Y-m-d` format — that mismatch is the flash. Passing
  // an already-formatted string makes the painted value match flatpickr's output.
  const formattedDate = useMemo(() => {
    const parsed = date instanceof Date ? date : new Date(date)
    return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : ''
  }, [date])

  return (
    <div className={classes}>
      {label && (
        <label>
          {label}
          {required && <span className={styles.required}> *</span>}
        </label>
      )}
      <Flatpickr
        value={formattedDate}
        onChange={(date: any) => onChange(date[0])}
        disabled={disabled}
        options={options}
      />
    </div>
  )
}

export default DatePicker
